import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Alert, Box, Button, Chip, CircularProgress, FormControlLabel, Paper, Stack, Switch, Tooltip, Typography, useTheme } from '@mui/material';
import { CheckCircleOutline, ErrorOutline, HelpOutline } from '@mui/icons-material';
import { postalSlipsApi } from '../../services/api';
import { compareOcrField, placeOcrBadges, storedOcrOverlay, validOcrFields } from '../../utils/postalOcrOverlay';

export default function PostalSlipImagePreview({ slip, imageUrl, imageReview, comparison, onImageLoad, onImageError, accountingError }) {
  const theme = useTheme();
  const viewportRef = useRef(null);
  const [viewportWidth, setViewportWidth] = useState(400);
  const [naturalSize, setNaturalSize] = useState(null);
  const [showBadges, setShowBadges] = useState(true);
  const [rotation, setRotation] = useState(0);
  const [zoom, setZoom] = useState(1);
  const [loadedUrl, setLoadedUrl] = useState(null);
  const [imageError, setImageError] = useState('');
  const stored = useMemo(() => storedOcrOverlay(slip, imageReview?.version), [slip, imageReview?.version]);
  const overlayQuery = useQuery({
    queryKey: ['postal-slip-ocr-overlay', slip.id, imageReview?.version],
    queryFn: async () => {
      const response = await postalSlipsApi.getOcrOverlay(slip.id, imageReview.version);
      if (response.data.data?.image_hash !== imageReview.version) throw new Error('La scansione è cambiata. Riaprire il bollettino.');
      return response.data.data;
    },
    enabled: Boolean(showBadges && loadedUrl === imageUrl && imageUrl && imageReview?.available && !stored && !imageError),
    staleTime: Infinity, gcTime: 30 * 60 * 1000, retry: false
  });
  const overlay = stored || (overlayQuery.data?.image_hash === imageReview?.version ? overlayQuery.data : null);
  useEffect(() => { setLoadedUrl(null); setNaturalSize(null); setImageError(''); setRotation(0); setZoom(1); }, [imageUrl]);
  useEffect(() => {
    const node = viewportRef.current;
    if (!node) return;
    const update = () => setViewportWidth(node.clientWidth || 400);
    update();
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(update); observer.observe(node);
    return () => observer.disconnect();
  }, [imageUrl]);

  const rotated = rotation % 180 !== 0;
  const naturalWidth = naturalSize?.width || imageReview?.width || 1300;
  const naturalHeight = naturalSize?.height || imageReview?.height || 800;
  const scale = Math.min(Math.max(100, viewportWidth - 24) / (rotated ? naturalHeight : naturalWidth), 376 / (rotated ? naturalWidth : naturalHeight));
  const width = naturalWidth * scale, height = naturalHeight * scale;
  const fields = validOcrFields(overlay?.layout).map(field => ({ ...field, ...compareOcrField(field, comparison, overlay.conflicts) }));
  const badges = placeOcrBadges(fields, width, height);
  const annotatedHeight = showBadges ? Math.max(height, ...badges.map(badge => badge.y + 26)) : height;
  const stageWidth = (rotated ? annotatedHeight : width) * zoom + 24;
  const stageHeight = (rotated ? width : annotatedHeight) * zoom + 24;

  return <Paper sx={{ p: 2, minWidth: 0, bgcolor: 'grey.100', minHeight: 300 }}>
    <Typography variant="subtitle1" gutterBottom>Verifica immagine del bollettino</Typography>
    {!imageReview && !accountingError && <CircularProgress size={22} />}
    {imageReview && !imageReview.available && <Alert severity="error">{imageReview.error}</Alert>}
    {imageError && <Alert severity="error">{imageError}</Alert>}
    {imageUrl && <>
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
        <Button size="small" onClick={() => setZoom(value => Math.min(3, value + 0.25))}>Ingrandisci</Button>
        <Button size="small" onClick={() => setZoom(value => Math.max(0.5, value - 0.25))}>Riduci</Button>
        <Button size="small" onClick={() => setRotation(value => (value + 90) % 360)}>Ruota</Button>
        <Button size="small" onClick={() => window.open(imageUrl, '_blank', 'width=1200,height=800,scrollbars=yes,resizable=yes')}>Apri originale</Button>
      </Stack>
      <FormControlLabel sx={{ m: 0, mb: 1 }} control={<Switch size="small" checked={showBadges} onChange={event => setShowBadges(event.target.checked)} />} label="Mostra badge OCR" />
      <Box ref={viewportRef} sx={{ overflow: 'auto', height: 400 }}>
        <Box sx={{ width: Math.max(viewportWidth, stageWidth), height: Math.max(400, stageHeight), position: 'relative' }}>
          <Box data-testid="postal-image-plane" sx={{ position: 'absolute', left: '50%', top: '50%', width, height: annotatedHeight,
            transform: `translate(-50%, -50%) rotate(${rotation}deg) scale(${zoom})`, transformOrigin: 'center' }}>
            <Box component="img" key={imageUrl} src={imageUrl} alt="Bollettino postale"
              onLoad={event => {
                setLoadedUrl(imageUrl); setImageError('');
                setNaturalSize({ width: event.target.naturalWidth, height: event.target.naturalHeight });
                onImageLoad();
              }}
              onError={() => {
                const message = 'Immagine non caricata. Riaprire il bollettino o recuperare la scansione originale.';
                setLoadedUrl(null); setImageError(message); onImageError(message);
              }} sx={{ display: 'block', width, height }} />
            {showBadges && loadedUrl === imageUrl && !imageError && badges.map(badge => {
              const color = theme.palette[badge.color].main;
              return <React.Fragment key={badge.id}>
                <Box sx={{ position: 'absolute', left: badge.box.x * width, top: badge.box.y * height,
                  width: badge.box.width * width, height: badge.box.height * height, border: `1px solid ${color}`, pointerEvents: 'none' }} />
                <svg width={width} height={annotatedHeight} style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }} aria-hidden="true">
                  <line x1={(badge.box.x + badge.box.width / 2) * width} y1={(badge.box.y + badge.box.height) * height}
                    x2={badge.x + Math.min(badge.width / 2, 20)} y2={badge.y} stroke={color} />
                </svg>
                <Tooltip title={`${badge.label}. ${badge.message}`}>
                  <Chip size="small" color={badge.color} label={badge.label} aria-label={`${badge.label}. ${badge.message}`}
                    icon={badge.color === 'success' ? <CheckCircleOutline /> : badge.color === 'error' ? <ErrorOutline /> : <HelpOutline />}
                    sx={{ position: 'absolute', left: badge.x, top: badge.y, maxWidth: badge.width, height: 22, fontSize: 11, zIndex: 1,
                      boxShadow: 1, '& .MuiChip-label': { px: 0.75 }, '& .MuiChip-icon': { fontSize: 14 } }} />
                </Tooltip>
              </React.Fragment>;
            })}
          </Box>
        </Box>
      </Box>
      {showBadges && <Box sx={{ mt: 1 }}>
        {overlayQuery.isFetching && !stored && <Stack direction="row" spacing={1} alignItems="center"><CircularProgress size={14} /><Typography variant="caption">Posizionamento badge OCR…</Typography></Stack>}
        {overlayQuery.isError && !stored && <Alert severity="warning" action={<Button size="small" onClick={() => overlayQuery.refetch()}>Riprova</Button>}>Badge OCR non disponibili. Puoi controllare la scansione originale.</Alert>}
        {overlay && fields.length === 0 && <Typography variant="caption">Nessun campo OCR posizionabile su questa scansione.</Typography>}
        {fields.length > 0 && <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Typography variant="caption" color="success.main">✓ Esatta</Typography>
          <Typography variant="caption" color="warning.dark">? Parziale / da verificare</Typography>
          <Typography variant="caption" color="error.main">× Discordante</Typography>
        </Stack>}
      </Box>}
    </>}
  </Paper>;
}
